(() => {
  'use strict';
  if (customElements.get('product-newsletter')) return;

  const formId = 'Y9Fygy';
  const storageKey = 'platadepalo:newsletter-submitted';
  const instances = new Set();
  let submitted = false;
  try {
    submitted = localStorage.getItem(storageKey) === '1';
  } catch (_) {
    // Storage can be unavailable; Klaviyo targeting still determines eligibility.
  }

  class ProductNewsletter extends HTMLElement {
    connectedCallback() {
      // This callback also runs when Impulse inserts product blocks into quick view.
      this.trigger = this.querySelector('.lead-magnetpdp__trigger');
      this.panel = this.querySelector('.lead-magnetpdp__panel');
      this.inner = this.querySelector('.lead-magnetpdp__panel-inner');
      this.embed = this.querySelector(`[data-newsletter-form="${formId}"]`);
      if (!this.trigger || !this.panel || !this.inner || !this.embed) return;

      this.isOpen = false;
      this.keepConfirmation = false;
      this.toggleHandler = () => {
        if (this.isOpen) {
          this.close();
          this.keepConfirmation = false;
          this.refresh();
        } else if (this.classList.contains('is-eligible')) {
          this.isOpen = true;
          this.panel.inert = false;
          this.panel.classList.add('is-open');
          this.panel.setAttribute('aria-hidden', 'false');
          this.trigger.setAttribute('aria-expanded', 'true');
          this.updateHeight();
          this.embed.querySelector('input[type="email"]')?.focus({ preventScroll: true });
        }
      };
      this.trigger.addEventListener('click', this.toggleHandler);
      this.observer = new MutationObserver(() => this.refresh());
      this.observer.observe(this.embed, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class', 'hidden', 'aria-hidden', 'disabled', 'type']
      });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateHeight());
        this.resizeObserver.observe(this.inner);
      }
      this.modal = this.closest('.modal--quick-shop');
      this.modalCloseHandler = () => {
        this.close();
        this.keepConfirmation = false;
        this.refresh();
      };
      if (this.modal) {
        this.modalCloseEvent = `modalClose.${this.modal.id}`;
        document.addEventListener(this.modalCloseEvent, this.modalCloseHandler);
      }
      instances.add(this);
      this.close();
      this.refresh();
    }

    disconnectedCallback() {
      this.trigger?.removeEventListener('click', this.toggleHandler);
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      if (this.modalCloseEvent) {
        document.removeEventListener(this.modalCloseEvent, this.modalCloseHandler);
      }
      instances.delete(this);
    }

    hasUsableForm() {
      // Klaviyo can insert empty wrappers or styles even when a visitor is excluded.
      // A real email field and action must exist; never force-open an excluded form.
      const email = this.embed.querySelector('form input[type="email"]');
      if (!email || email.disabled || !email.closest('form').querySelector('button')) return false;
      for (let node = email; node && node !== this.embed; node = node.parentElement) {
        const style = getComputedStyle(node);
        if (node.hidden || node.getAttribute('aria-hidden') === 'true' ||
            style.display === 'none' || style.visibility === 'hidden') return false;
      }
      return true;
    }

    refresh() {
      const eligible = this.keepConfirmation || (!submitted && this.hasUsableForm());
      this.classList.toggle('is-eligible', eligible);
      this.inert = !eligible;
      this.setAttribute('aria-hidden', String(!eligible));
      if (!eligible) this.close();
      this.updateHeight();
    }

    close() {
      this.isOpen = false;
      this.panel.inert = true;
      this.panel.classList.remove('is-open');
      this.panel.style.maxHeight = '0px';
      this.panel.setAttribute('aria-hidden', 'true');
      this.trigger.setAttribute('aria-expanded', 'false');
    }

    updateHeight() {
      if (!this.isOpen) return;
      const height = `${this.inner.scrollHeight}px`;
      if (this.panel.style.maxHeight !== height) this.panel.style.maxHeight = height;
    }
  }

  // No email or profile identifier is stored. Retain the open success/coupon view,
  // while suppressing this offer on other products and on subsequent visits.
  window.addEventListener('klaviyoForms', event => {
    const detail = event.detail || {};
    if (detail.formId !== formId) return;
    if (detail.type === 'submit') {
      submitted = true;
      try { localStorage.setItem(storageKey, '1'); } catch (_) { /* Optional storage. */ }
      instances.forEach(instance => {
        instance.keepConfirmation = instance.isOpen &&
          (!instance.modal || instance.modal.classList.contains('modal--is-active'));
      });
    }
    instances.forEach(instance => instance.refresh());
  });
  window.addEventListener('storage', event => {
    if (event.key !== storageKey || event.newValue !== '1') return;
    submitted = true;
    instances.forEach(instance => instance.refresh());
  });
  customElements.define('product-newsletter', ProductNewsletter);
})();
